import { cn } from '@invent/wl-ui-kit-next';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton
} from '@invent/wl-ui-kit-next/ui';
import { CircleHelp } from 'lucide-react';
import * as React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { PageHeader, RecordSection, toListTop } from '@/design';
import { errorMessage } from '@/domains/api';
import { TEAM_PATHS } from '../paths';

/**
 * The frame every Team Portal screen shares: the page header, the section's tabs, the load and
 * error states, and the paging control the real lists need.
 *
 * It exists so the sections cannot drift apart while they are all this young. `BUILDER_TABS`
 * lives here too, rendered by the three screens of the survey builder, `SEC_ADV_TABS` beside it
 * for the SEC ADV register's screens, and `REFRESH_TABS` for the BrokerCheck refresh's.
 *
 * There is no "this data is mocked" notice any more, because there is no screen it would be
 * true of: every list here reads survey-api. The one card that still does not — the
 * cross-version diff — says so on itself, which is the right size for a claim about one card.
 */

export interface IAdminTab {
  to: string;
  label: string;
  /** Match this path exactly — the index tab, which every sibling path starts with. */
  end?: boolean;
}

export function AdminPage({
  title,
  crumb,
  tabs,
  actions,
  media,
  meta,
  lede,
  documentTitle,
  children
}: {
  title: string;
  /** The area a record belongs to — "People" over a person's name. */
  crumb?: React.ReactNode;
  tabs?: IAdminTab[];
  /** What the page does, at the header's right. */
  actions?: React.ReactNode;
  /** A picture before the title — the person hub's photo. */
  media?: React.ReactNode;
  /** The record's key facts under the title — a ranking's highlights. */
  meta?: React.ReactNode;
  /** A line under the title, beside the actions — a register firm's place and numbers. */
  lede?: React.ReactNode;
  /** The tab's title where the page's title would not tell it from a sibling's — the refresh's
   *  Runs tab beside its Schedule, one run beside another (`PageHeader`'s, F11's R5). */
  documentTitle?: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <PageHeader
        title={title}
        crumb={crumb}
        media={media}
        meta={meta}
        lede={lede}
        documentTitle={documentTitle}
        actions={actions}
        tabs={tabs && tabs.length > 0 ? <SectionTabs tabs={tabs} /> : undefined}
      />

      <div className="grid gap-6 px-6 py-6">{children}</div>
    </>
  );
}

/**
 * Routed tabs, not `Tabs` from the kit: each pane is its own URL, so a screen can be
 * linked to, reloaded and driven by a test. The kit's tab primitive holds its state in the
 * component, which would make all three builder screens one unlinkable page. They sit on the
 * header's bottom edge, the current one underlined in the Team Portal's Teal.
 *
 * A tab carries the history entry's state to its sibling: a record opened from its list keeps the
 * list's search there, and a pane of it is still that record, drawn beside that list.
 */
export function SectionTabs({ tabs }: { tabs: IAdminTab[] }) {
  const { state } = useLocation();
  return (
    <nav aria-label="Section" className="-mx-1 flex flex-wrap gap-x-4">
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          state={state}
          className={({ isActive }) =>
            cn(
              '-mb-px border-b-2 px-1 pt-1 pb-2 text-[0.8125rem] transition-colors',
              isActive
                ? 'border-[var(--color-teal)] font-medium text-[var(--color-royal-blue)]'
                : 'border-transparent text-muted-foreground hover:text-[var(--color-royal-blue)]'
            )
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * The survey builder's three panes. Kept here rather than in one of the pages so none of them
 * owns the other two, and each is a URL — see `SectionTabs` on why they are routes. Ranking
 * cycles were the fourth until the two portals gave them a Setup entry of their own: repointing a
 * live cycle is a different job from authoring a definition (the design's §4.4).
 */
export const BUILDER_TABS = [
  { to: TEAM_PATHS.questions, label: 'Question bank' },
  { to: TEAM_PATHS.options, label: 'Option lists' },
  { to: TEAM_PATHS.versions, label: 'Versions' }
];

/**
 * Staff fields' two panes (SR-226): the catalogue — what a field is — and the forms, where one
 * survey's sections and the order of its fields are set. Two because they are two jobs: a field
 * is global and its placement is a survey's, and survey-api splits its routes on the same line.
 */
export const STAFF_FIELD_TABS = [
  { to: TEAM_PATHS.staffFieldCatalogue, label: 'Fields' },
  { to: TEAM_PATHS.staffFieldForms, label: 'Forms' },
  { to: TEAM_PATHS.noteInitials, label: 'Note initials' }
];

/** The SEC ADV register's two screens — the firm list (a card is under it) and the state. */
export const SEC_ADV_FIRMS_PATH = TEAM_PATHS.secAdvFirms;
export const SEC_ADV_STATUS_PATH = TEAM_PATHS.secAdvStatus;

/**
 * The SEC ADV register's two panes — its firms and its own state — for the metadata tabs'
 * reason. A firm's card is under the Firms tab's path, so that tab stays lit on a card.
 */
export const SEC_ADV_TABS = [
  { to: SEC_ADV_FIRMS_PATH, label: 'Firms' },
  { to: SEC_ADV_STATUS_PATH, label: 'Register status' }
];

/** The BrokerCheck refresh's two screens (SR-222, F10's Q1) — its schedule, the area's root, so
 *  its tab matches exactly, and its runs, a run's record under them. */
export const REFRESH_TABS = [
  { to: TEAM_PATHS.refresh, label: 'Schedule', end: true },
  { to: TEAM_PATHS.refreshRuns, label: 'Runs' }
];

/** The way to the register's state from a sentence that depends on it — whether its imports
 *  have run, whether it is fresh — named as the tab is. */
export function RegisterStatusLink() {
  return (
    <Link to={SEC_ADV_STATUS_PATH} className="underline">
      Register status
    </Link>
  );
}

/**
 * One labelled read-only value — the shape a response, an account and a participant lay their
 * facts out in.
 *
 * The value is a block, so it may hold a list or a paragraph as well as a word. It wraps
 * **anywhere**, and only where it has to: an id, an address or a hash has no space to break at
 * and must not widen a card past a phone's width, while a sentence beside it breaks between its
 * words. `break-all` did the first and split every sentence's words mid-letter; `break-words`
 * keeps the words but does not lower the content's minimum width, so a long address would still
 * push the card out on a phone.
 *
 * Label and value sit at the top of their cell (`content-start`): a fact beside a taller one —
 * the hub's Photo portrait — is stretched to the row's height, and a stretched grid spreads its
 * two rows apart, leaving the value floating halfway down.
 */
export function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid content-start gap-0.5">
      <span className="text-xs tracking-wide text-muted-foreground uppercase">{label}</span>
      <div className="wrap-anywhere">{value}</div>
    </div>
  );
}

/**
 * A card that is a named region, so each part of a page can be found by what it is called —
 * by a reader moving by landmarks, and by a spec. Its title is a level-2 heading under the
 * page's own. The person hub and the SEC ADV register's pages lay their parts out in it.
 *
 * `anchor` makes it a place a section nav scrolls to (`section-nav.tsx`), clear of the header
 * and the nav's own bar where they are sticky; `description` is optional because the hub's sections explain a state where they
 * need to and the screen not at all (the assessment's §2.2).
 *
 * **Inside `SectionFolds` it folds** — a level-2 `RecordSection` whose heading is the summary, open
 * by default, remembered per viewer under the provider's page as `section.<anchor>` (the advisor
 * record page's F11, Q2 ruled (a): the hub's sections fold as the Lightning page's do). The
 * region, its name and the anchor are the same either way, so a spec's `section()` finds both
 * arms. A provider, rather than a prop at each of the hub's fifteen call sites: the hub decides
 * that its sections fold, and the response view, which draws the same card under its own nav,
 * stays a card. The body is always mounted — a section's reads are made by its component, around
 * the card — so a folded section reads exactly as an open one does.
 */
export const SectionFolds = React.createContext<Nullable<string>>(null);

/**
 * The heading level a `Section` drawn as a card takes — 2, a page's own section, unless a screen
 * draws another screen's cards inside one of its sections: a firm's record page draws the SEC ADV
 * card's under its SEC ADV fold and a firm's name (SR-391), so they are level 4 there.
 */
export const SectionLevel = React.createContext<2 | 3 | 4>(2);

export function Section({
  title,
  description,
  anchor,
  empty = false,
  aside,
  children
}: {
  title: string;
  description?: React.ReactNode;
  anchor?: string;
  /** A small press drawn in a folded section's title row, beside its "?" — a "Read again" that
   *  would otherwise take a line of its own (SR-407). */
  aside?: React.ReactNode;
  /** A folded section with nothing to show (SR-407): its title and "(0)", folded — see
   *  `RecordSection`'s `empty`. A card (no fold) draws its content as ever. */
  empty?: boolean;
  children: React.ReactNode;
}) {
  const id = React.useId();
  const foldPage = React.useContext(SectionFolds);
  const level = React.useContext(SectionLevel);
  if (foldPage !== null && anchor) {
    return (
      <RecordSection
        page={foldPage}
        sectionKey={`section.${anchor}`}
        title={title}
        level={2}
        defaultOpen
        filled={0}
        total={0}
        anchor={anchor}
        flush
        empty={empty}
      >
        {/* A card inside this one is a card again, never a second fold under this one's key. */}
        <SectionFolds.Provider value={null}>
          {(description || aside) && (
            <AboutSection title={title} aside={aside}>
              {description}
            </AboutSection>
          )}
          {children}
        </SectionFolds.Provider>
      </RecordSection>
    );
  }
  return (
    <Card
      role="region"
      aria-labelledby={id}
      id={anchor}
      className={cn(anchor && 'scroll-mt-4 lg:scroll-mt-[calc(3.5rem+var(--section-bar-h,2.5rem))]')}
    >
      <CardHeader>
        <CardTitle id={id} role="heading" aria-level={level}>
          {title}
        </CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      {children}
    </Card>
  );
}

/**
 * A folded section's description, behind a "?" at the head of its body (SR-407): what a section
 * is and where its data comes from is read once, and drawn above every section it pushed the
 * section's own content a paragraph down — Salesforce's related lists carry no such line.
 */
function AboutSection({
  title,
  aside,
  children
}: {
  title: string;
  aside?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    // Lifted into the fold's title row, outside its <summary>, so a press never folds it.
    <div className="relative h-0">
      <div className="absolute -top-11 right-4 flex items-center gap-1">
        {aside}
        {children && <About title={title}>{children}</About>}
      </div>
    </div>
  );
}

function About({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`About ${title}`}
          className="h-6 px-1 text-muted-foreground"
        >
          <CircleHelp className="size-4" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="max-w-sm text-sm">
        {children}
      </PopoverContent>
    </Popover>
  );
}

/**
 * The paging control every oversight list shares.
 *
 * A page number and a total, not an infinite scroll: survey-api pages this surface with a
 * 1-based `page` and a hard `pageSize` ceiling of 100, orders every list itself, and offers
 * no cursor — so the control that matches the contract is the one that says which page of
 * how many this is. Rendered even for a single page, so a reader can see the total without
 * having to reach the end of a scroll to learn there was none.
 */
export function Pager({
  page,
  pageSize,
  total,
  onPage,
  busy,
  quiet = false,
  onMore
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  busy?: boolean;
  /** Draw nothing while the whole list fits on its first page — a record page's related lists
   *  (SR-407), where "1–3 of 3" and two dead buttons under every section say nothing. A page
   *  past the end still draws, since that is when it says something. */
  quiet?: boolean;
  /** "Show more" — the page made longer by the caller (`useListPaging`), drawn while rows follow
   *  the page on screen (SR-407). */
  onMore?: () => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  // A page past the last — a pasted or stale address — holds nothing of a list that is not empty,
  // and "201–12 of 12" would say otherwise.
  const pastTheEnd = total > 0 && first > total;
  if (quiet && page === 1 && lastPage === 1) return null;

  // A new page starts at the list's top, as on a work page (SR-503); Show more keeps the place.
  const turn = (target: number) => (event: React.MouseEvent<HTMLElement>) => {
    toListTop(event.currentTarget.closest('div'));
    onPage(target);
  };
  return (
    <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
      <span>
        {total === 0
          ? 'Nothing to show'
          : pastTheEnd
            ? `Nothing on page ${page} · ${total} in all`
            : `${first}–${last} of ${total}`}
        {!pastTheEnd && lastPage > 1 && ` · page ${page} of ${lastPage}`}
      </span>
      <span className="flex gap-2">
        {onMore && !pastTheEnd && last < total && (
          <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onMore}>
            Show more
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || page <= 1}
          onClick={turn(page - 1)}
        >
          Previous
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || page >= lastPage}
          onClick={turn(page + 1)}
        >
          Next
        </Button>
      </span>
    </div>
  );
}

/**
 * What an empty page of a list that is **not** empty says: that the page is past the last one —
 * as a pasted or stale address can ask for — how many there are, on how many pages, and the way
 * to the last. A list's own "nothing here" sentence would claim, wrongly, that it holds nothing.
 */
export function PastTheEnd({
  page,
  pageSize,
  total,
  counted,
  onPage
}: {
  page: number;
  pageSize: number;
  total: number;
  /** What `total` counts, one and many — `['firm', 'firms']`. */
  counted: readonly [string, string];
  onPage: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  return (
    <>
      Page {page} is past the end — there {total === 1 ? 'is' : 'are'} {total}{' '}
      {total === 1 ? counted[0] : counted[1]}, on {lastPage} {lastPage === 1 ? 'page' : 'pages'}.{' '}
      <Button type="button" variant="outline" size="sm" onClick={() => onPage(lastPage)}>
        Last page
      </Button>
    </>
  );
}

/** A list's page number in our own URL — not a server rule: anything that is not a page is
 *  page 1. */
export function pageOf(raw: Nullable<string>): number {
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1;
}

/** The URL's search params turned to another page — page 1 by leaving `page` out, which is how
 *  a fresh query reads, so the first page has one address rather than two. */
export function withPage(params: URLSearchParams, target: number): URLSearchParams {
  const next = new URLSearchParams(params);
  if (target === 1) next.delete('page');
  else next.set('page', String(target));
  return next;
}

/**
 * A repeated search parameter with one of its checkboxes switched: the values this build
 * knows, in its order, then every value the URL named that it does not know — kept and sent,
 * so survey-api's 400 says what is wrong with it. A checkbox that quietly dropped one would be
 * answering a different query than the address bar shows. Where a list draws a box for an unknown
 * value (`withUnknown`), unticking it takes the value out like any other, and the box goes with it.
 * People's `survey`, the queues' `trigger`, the registrations queue's `status` and the SEC ADV
 * register's `firmType` all repeat this way.
 */
export function toggleRepeated(
  params: URLSearchParams,
  name: string,
  known: readonly string[],
  value: string,
  on: boolean
): void {
  const current = params.getAll(name);
  // The value switched follows its box; every other value stays as the URL named it.
  const kept = (candidate: string) => (candidate === value ? on : current.includes(candidate));
  params.delete(name);
  for (const candidate of known) {
    if (kept(candidate)) params.append(name, candidate);
  }
  for (const candidate of current) {
    if (!known.includes(candidate) && kept(candidate)) params.append(name, candidate);
  }
}

/** The known values, then every value named that is not among them, as spelled — the boxes a
 *  repeated filter draws, so a value the URL named is still offered, ticked. */
export function withUnknown(known: readonly string[], named: readonly string[]): string[] {
  return [...known, ...named.filter((value) => !known.includes(value))];
}

/**
 * A filter of a few exclusive positions, as radios — a group named by its label, the label shown
 * beside the positions rather than read twice. The SEC ADV register's "Participants" on the firm
 * list and "Kind" on the import log are two of these; each position's value is what the screen
 * puts in its URL, compared as it is. So the position that narrows nothing is a value nothing sent
 * can be — `null` (`IRadioOption`), `undefined`, or `''` where the client drops it — never a string
 * standing for it, which an address can spell. A position is keyed by its place: a value the URL
 * names is drawn as spelled, and may read as an option's label (`?teamLeadShared=Any`).
 */
export function RadioFilter<V>({
  label,
  name,
  options,
  value,
  onChoose
}: {
  label: string;
  /** The radios' shared `name`, unique on the page. */
  name: string;
  options: readonly { value: V; label: string }[];
  value: V;
  onChoose: (value: V) => void;
}) {
  return (
    <fieldset aria-label={label} className="flex flex-wrap items-center gap-4">
      <span className="text-muted-foreground" aria-hidden>
        {label}
      </span>
      {options.map((option, index) => (
        <label key={index} className="flex items-center gap-2">
          <input
            type="radio"
            name={name}
            className="accent-[var(--color-sapphire)]"
            checked={value === option.value}
            onChange={() => onChoose(option.value)}
          />
          {option.label}
        </label>
      ))}
    </fieldset>
  );
}

/**
 * What went wrong, for a write the mock refused.
 *
 * Deliberately not `errorMessage` from `@/domains/api`: that one is `(error as Error)
 * ?.message || fallback`, which reads a `message` off any object and swallows an `Error`
 * whose message is empty. The one remaining mock throws plain `Error`s, so anything that is
 * not an `Error` here is a bug in this app rather than a refusal to report. A screen talking
 * to survey-api wants `refusalMessage` instead — it reads the ProblemDetails body.
 */
export function adminErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function LoadError({ message }: { message: string }) {
  return (
    <Alert variant="destructive">
      <AlertTitle>Something went wrong</AlertTitle>
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="grid gap-2">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  );
}

/**
 * Every admin screen loads, re-reads after a write, and aborts on unmount, so the three
 * states and that lifecycle live here rather than being typed out per page.
 *
 * **`deps` is what a filter, a page number or a route parameter goes in.** `load` itself is
 * held in a ref, so a page may pass an inline lambda without re-fetching on every render —
 * but that also means changing what the lambda closes over does *not* re-fetch by itself.
 * A screen whose query can change has to say what it changes with, exactly as it would to
 * `useEffect`. Omitting it is a silent stale read, which is why this parameter exists as a
 * named thing rather than as an implicit dependency on `load`'s identity: that alternative
 * fails by fetching in an infinite loop the first time someone forgets a `useCallback`.
 *
 * **The newest read begun is the one kept.** A screen reads on load, after its own writes, and
 * — the person hub — by itself while an answer is on its way, so two reads can be out at
 * once, and the network does not answer them in order. An answer that lands after a later read
 * was begun is dropped, whatever it says: otherwise a read begun before a write, landing after
 * the write's own re-read, puts the screen back to how it was before the write.
 *
 * A caller-less `reload()` is cancelled with the current load's controller, so leaving the
 * screen or changing its query drops it too. It resolves `false` only when it failed and was
 * still the newest read — the one case a caller has something to say about; a read overtaken
 * by a newer one resolves `true`, since the newer one's outcome is what the screen shows.
 *
 * **`data` is not cleared when `deps` change**: a list keeps its last page on screen until the
 * next lands. A screen whose parameter names a different *thing* — a participant, not a page of
 * one — remounts on it instead (`key`), so nothing of the last one is left to be read or acted
 * on, and `data` null when a read throws means the first read of that thing failed.
 *
 * **`adopt(value)` takes a write's answer as the read** — a correction's 200 is the whole detail
 * (SR-222), so re-reading it would only ask again for what just arrived. It counts as the newest
 * read begun: a read still in flight lands overtaken and is dropped, as it would be under a later
 * read, and cannot put the screen back to how it was before the write.
 *
 * **`reading` is whether the newest read begun is still out** — what a list shows while its last
 * page stays on screen under a query not yet answered (People, Teams, Firms). It falls when that
 * read lands or fails, or when `adopt` takes its place; an overtaken read leaves it to the newer.
 */
export function useAdminData<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: React.DependencyList = []
) {
  const [data, setData] = React.useState<Nullable<T>>(null);
  const [error, setError] = React.useState<Nullable<string>>(null);
  const [reading, setReading] = React.useState(false);
  const loadRef = React.useRef(load);
  loadRef.current = load;
  const current = React.useRef<Nullable<AbortController>>(null);
  const begun = React.useRef(0);

  const reload = React.useCallback(async (signal?: AbortSignal): Promise<boolean> => {
    const effective = signal ?? current.current?.signal ?? new AbortController().signal;
    const mine = ++begun.current;
    const overtaken = () => effective.aborted || mine !== begun.current;
    setReading(true);
    try {
      const next = await loadRef.current(effective);
      if (overtaken()) return true;
      setData(next);
      setError(null);
      setReading(false);
      return true;
    } catch (loadError) {
      if (overtaken()) return true;
      // `errorMessage` over `.message`: survey-api's refusals carry their reason in the
      // ProblemDetails `detail`, and a screen that shows only the title turns "unknown
      // survey code 'RIA'" into "The query names things this endpoint does not offer."
      setError(errorMessage(loadError, 'Could not load this page'));
      setReading(false);
      return false;
    }
    // `deps` is spread deliberately: this identity is what the effect below re-runs on.
    // eslint is not configured in this repo, so nothing rewrites this array behind us.
  }, deps);

  const adopt = React.useCallback((value: T) => {
    begun.current += 1;
    setData(value);
    setError(null);
    setReading(false);
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();
    current.current = controller;
    void reload(controller.signal);
    return () => controller.abort();
  }, [reload]);

  return { data, error, setError, reload, adopt, reading };
}

/**
 * A text filter's value, held back until the typing stops.
 *
 * Only needed where the **server** filters. The question bank's search box holds every row
 * in memory and can re-filter per keystroke for free; a server-side substring filter cannot
 * — `useAdminData` would fire a request per character, and since each one aborts the last,
 * the list would also flicker empty while someone types a word.
 *
 * Returned rather than written back into the caller's state, so the input stays fully
 * controlled and responsive: the box shows what was typed immediately, and only the query
 * lags.
 */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [settled, setSettled] = React.useState(value);

  React.useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return settled;
}
